import React, { FC, useEffect, useState } from 'react';
import {
  TextInput,
  Button,
  ButtonColor,
  ButtonSize,
  InputType,
  Modal,
  ModalType,
} from '@code/innovance-react-ui-kit';
import { useHistory } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import Crypto from 'crypto-js';

import {
  useUserInfoState,
  useUserInfoDispatch,
  useMenuDispatch,
} from 'src/front/contexts/SessionContext';

import { DASHBOARD } from 'src/front/routes/paths';
import { User } from 'src/front/api/apiInstance';

import Markdown from 'src/front/components/Markdown';
import {
  Container,
  Title,
  Separator,
  FormRow,
  FormElement,
  ButtonsRow,
} from 'src/front/components/FormStyles';

import { AES_SECRET_KEY } from 'src/front/common/constants/general';
import useYupResolver from 'src/front/common/hooks/useYupResolver';
import { ChangePasswordSchema } from 'src/front/common/validations/changePassword';
import { formatBlank } from 'src/front/common/utils/formUtils';
import { isPasswordCorrect } from 'src/front/common/utils/errorUtils';

const Description = styled.div`
  display: flex;

  color: ${({ theme }) => theme.palette.lipstick};
`;

const defaultValues = {
  oldPassword: '',
  newPassword: '',
  newPasswordConfirm: '',
};

interface ChangePasswordFormData {
  oldPassword: string;
  newPassword: string;
  newPasswordConfirm: string;
}

const encrypt = (text = ''): string => {
  const key = Crypto.enc.Base64.parse(AES_SECRET_KEY);
  const result = Crypto.AES.encrypt(text, key, {
    mode: Crypto.mode.ECB,
    padding: Crypto.pad.Pkcs7,
  }).toString();

  return result;
};

const ChangePassword: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const userInfoState = useUserInfoState();
  const setUserInfo = useUserInfoDispatch();
  const setMenu = useMenuDispatch();

  const { isAuthenticationRestricted } = userInfoState;

  const resolver = useYupResolver(ChangePasswordSchema, { todayMaxControl: true });

  const { watch, register, errors, setValue, handleSubmit, formState, setError } = useForm<
    ChangePasswordFormData
  >({
    defaultValues,
    resolver,
  });

  const [isSuccess, setSuccess] = useState<boolean>(false);

  useEffect(() => {
    register({ name: 'oldPassword' });
    register({ name: 'newPassword' });
    register({ name: 'newPasswordConfirm' });
  }, [register]);

  const handleTextChange = (fieldName: string) => (text: string) =>
    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore
    setValue(fieldName, text, {
      shouldValidate: formState.isSubmitted || !!text,
      shouldDirty: formState.isSubmitted || !!text,
    });

  const onSubmit = async (formData: ChangePasswordFormData): Promise<void> => {
    try {
      const { oldPassword, newPassword, newPasswordConfirm } = formData;
      const { data } = await User.changePassword({
        oldPassword: encrypt(oldPassword),
        newPassword: encrypt(newPassword),
        reNewPassword: encrypt(newPasswordConfirm),
      });

      if (data.result === 'OK') {
        setUserInfo({
          ...userInfoState,
          isAuthenticationRestricted: false,
        });
        setMenu(data.menuList || []);
        setSuccess(!!data.changePasswordResultMessage);
      }
    } catch (error) {
      if (isPasswordCorrect(error)) {
        setError('newPasswordConfirm', {
          type: 'pattern',
          message: error?.errorMessage,
        });
      }
    }
  };

  const goToDashboard = (): void => {
    history.push(DASHBOARD);
  };

  const handleResultModalClose = (): void => {
    setSuccess(false);
    goToDashboard();
  };

  return (
    <Container>
      <Title>
        <Trans i18nKey="passwordChange_title" />
      </Title>

      {isAuthenticationRestricted ? (
        <>
          <Separator />
          <Description>
            <Markdown text={t('passwordChange_expiredPassword_text')} />
          </Description>
        </>
      ) : null}

      <Separator />
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormRow>
          <FormElement>
            <TextInput
              errorMessage={t(errors?.oldPassword?.message as string)}
              formatter={formatBlank}
              label={t('passwordChange_oldPassword_label')}
              maxLength={15}
              name="oldPassword"
              onChange={handleTextChange('oldPassword')}
              type={InputType.Password}
              value={watch('oldPassword')}
            />
          </FormElement>
        </FormRow>
        <FormRow>
          <FormElement>
            <TextInput
              errorMessage={t(errors?.newPassword?.message as string)}
              formatter={formatBlank}
              label={t('passwordChange_newPassword_label')}
              maxLength={15}
              name="newPassword"
              onChange={handleTextChange('newPassword')}
              type={InputType.Password}
              value={watch('newPassword')}
            />
          </FormElement>
        </FormRow>
        <FormRow>
          <FormElement>
            <TextInput
              errorMessage={t(errors?.newPasswordConfirm?.message as string)}
              formatter={formatBlank}
              label={t('passwordChange_repeatNewPassword_label')}
              maxLength={15}
              name="newPasswordConfirm"
              onChange={handleTextChange('newPasswordConfirm')}
              type={InputType.Password}
              value={watch('newPasswordConfirm')}
            />
          </FormElement>
        </FormRow>
        <Separator />
        <Separator />
        <Separator />
        <ButtonsRow>
          {!isAuthenticationRestricted ? (
            <Button
              color={ButtonColor.Secondary}
              onClick={goToDashboard}
              size={ButtonSize.Medium}
              text={t('general_cancel_button')}
              type="button"
            />
          ) : null}

          <Button
            color={ButtonColor.Primary}
            disabled={formState.isSubmitting}
            size={ButtonSize.Medium}
            text={t('general_change_button')}
            type="submit"
          />
        </ButtonsRow>
      </form>
      {isSuccess ? (
        <Modal
          description={t('passwordChange_successMessage')}
          onCancel={handleResultModalClose}
          submitText={t('general_components_ok_button')}
          title={t('general_wellDone_text')}
          type={ModalType.Success}
        />
      ) : null}
    </Container>
  );
};

export default ChangePassword;
